import * as React from 'react';
import { createContext, ReactNode, useContext, useState } from 'react';

import { Message as AIMessage } from '@ai-sdk/react';

interface BuilderContextType {
  messages: AIMessage[];
  rightColumnVisible: boolean;
  selectedMessageId: string | null;
  selectedMessage: AIMessage | null;
  openContent: (messageId: string) => void;
  closeContent: () => void;
  input: string;
  handleInputChange: (value: string) => void;
  handleSubmit: (value: string) => void;
}

const BuilderContext = createContext<BuilderContextType | undefined>(undefined);

interface BuilderProviderProps {
  children: ReactNode;
  messages: AIMessage[];
  input: string;
  handleInputChange: (value: string) => void;
  handleSubmit: (value: string) => void;
}

export const BuilderProvider: React.FC<BuilderProviderProps> = ({
  children,
  messages,
  input,
  handleInputChange,
  handleSubmit
}) => {
  const [rightColumnVisible, setRightColumnVisible] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const selectedMessage = messages.find((msg) => msg.id === selectedMessageId) ?? null;

  const openContent = (messageId: string) => {
    setSelectedMessageId(messageId);
    setRightColumnVisible(true);
  };

  const closeContent = () => {
    setRightColumnVisible(false);
    setSelectedMessageId(null);
  };

  const value: BuilderContextType = {
    messages,
    rightColumnVisible,
    selectedMessageId,
    selectedMessage,
    openContent,
    closeContent,
    input,
    handleInputChange,
    handleSubmit
  };

  return <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>;
};

export const useBuilderContext = (): BuilderContextType => {
  const context = useContext(BuilderContext);
  if (context === undefined) {
    throw new Error('useBuilderContext must be used within a BuilderProvider');
  }
  return context;
};
